import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditPanel, useSaveWithToast } from "containers/settings/ui";
import { FuelBarcode, FuelBarcodeType } from "./fuelBarcodes.types";
import { saveFuelBarcode } from "./fuelBarcodes.service";

const INPUT_CLASS =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base md:text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200";

// What the person should read for each refusal code the server answers with.
const SAVE_ERROR_KEYS: Record<string, string> = {
  FUEL_BARCODE_DUPLICATE: "fuel_barcode_duplicate",
  MISSING_FUEL_BARCODE_VALUE: "fuel_barcode_value_required",
  COMPANY_USER_FUEL_PERMISSION_NOT_FOUND:
    "add_to_fuel_list_permission_required",
  COMPANY_USER_MASTER_NOT_FOUND: "add_to_fuel_list_permission_required",
};

// The label of the value field and the sentence under it depend on what kind of match is added.
const VALUE_TEXT_KEYS: Record<
  FuelBarcodeType,
  { label: string; help?: string }
> = {
  barcode: { label: "fuel_barcode_value", help: "fuel_barcode_value_help" },
  productClass: { label: "fuel_barcode_product_class_value" },
  serviceType: {
    label: "fuel_barcode_service_type_value",
    help: "fuel_barcode_service_type_help",
  },
};

interface FuelBarcodeEditorProps {
  companyId: string;
  /** The entry being changed; omitted when adding a new one. */
  barcode?: FuelBarcode;
  onSaved: () => void;
  onCancel: () => void;
}

/** Add or change one entry of the list of what counts as fuel, inline in the list's card. */
export function FuelBarcodeEditor({
  companyId,
  barcode,
  onSaved,
  onCancel,
}: FuelBarcodeEditorProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [type, setType] = useState<FuelBarcodeType>(barcode?.type ?? "barcode");
  const [value, setValue] = useState(barcode?.value ?? "");
  const [description, setDescription] = useState(barcode?.description ?? "");

  // The list can be long: bring the editor into view when it opens above it.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panelRef.current?.scrollIntoView({ block: "nearest" });
  }, []);

  const handleSave = async () => {
    // The active flag is not sent: the server keeps an entry's flag as it is
    // and starts new ones active, so a switch flipped in the table while this
    // editor is open is not overwritten.
    const saved = await save(
      () =>
        saveFuelBarcode(companyId, {
          id: barcode?.id,
          type,
          value: value.trim(),
          description: description.trim() || null,
        }),
      {
        errorMessage: (err) => {
          const code = (err as { response?: { data?: string } }).response?.data;
          return t(SAVE_ERROR_KEYS[code ?? ""] ?? "fuel_barcode_error_saving");
        },
      }
    );
    if (saved) onSaved();
  };

  const valueText = VALUE_TEXT_KEYS[type];

  return (
    <div ref={panelRef} className="mb-4">
      <EditPanel
        onSave={handleSave}
        onCancel={onCancel}
        saving={saving}
        error={error}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="fuelBarcodeType"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("fuel_barcode_type")}
            </label>
            <select
              id="fuelBarcodeType"
              className={INPUT_CLASS}
              value={type}
              onChange={(e) => setType(e.target.value as FuelBarcodeType)}
            >
              <option value="barcode">{t("fuel_barcode_type_barcode")}</option>
              <option value="productClass">
                {t("fuel_barcode_type_product_class")}
              </option>
              <option value="serviceType">
                {t("fuel_barcode_type_service_type")}
              </option>
            </select>
          </div>
          <div>
            <label
              htmlFor="fuelBarcodeValue"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t(valueText.label)}
            </label>
            <input
              id="fuelBarcodeValue"
              type="text"
              autoComplete="off"
              className={INPUT_CLASS}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            {valueText.help && (
              <p className="mt-1 text-xs text-gray-500">{t(valueText.help)}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label
              htmlFor="fuelBarcodeDescription"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("fuel_barcode_description")}
            </label>
            <input
              id="fuelBarcodeDescription"
              type="text"
              autoComplete="off"
              className={INPUT_CLASS}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </EditPanel>
    </div>
  );
}
